import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { LayoutSignUp, SignUpState } from 'src/domains/players/webview/components/SignUp';
import { RealexJourney } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexJourney';
import {
    SkipButton,
    SkipButtonContainer,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/Deposit.style';
import { PraxisJourney } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/praxisProvider/PraxisJourney';
import { PaysafeJourney } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/paysafeProvider/PaysafeJourney';
import { OperatorTurnOffDeposits } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/depositFailure/OperatorTurnOffDeposits';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { MultiDepositJourney } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositJourney';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';

interface DepositSignupLayoutPropsType {
    children: React.ReactElement;
    skipAction: () => void;
    signUpState: SignUpState;
}

export const DepositSignupLayout = observer(
    'DepositSignupLayout',
    ({ children, skipAction, signUpState }: DepositSignupLayoutPropsType): JSX.Element => {
        const common = useCommon();
        const googleTagManager = GoogleTagManagerState.get(common);
        const handleSkipAction = (): void => {
            skipAction();
            googleTagManager.gtmSignUpStepFour(0);
        };

        return (
            <LayoutSignUp
                stepsCounter={true}
                footer={true}
                signUpState={signUpState}
            >
                <>
                    {children}
                    <SkipButtonContainer>
                        <SkipButton
                            version='secondary-dark'
                            size='medium'
                            onClick={handleSkipAction}
                            dataTest='skip-button'
                        >
                            <I18n
                                langKey='signup-journey.deposit.skip-button'
                                defaultText='Skip'
                            />
                        </SkipButton>
                    </SkipButtonContainer>
                </>
            </LayoutSignUp>
        );
    }
);

interface PropsType {
    signUpState: SignUpState;
}

export const DepositSignupProvider = observer('DepositSignupProvider', ({ signUpState }: PropsType): JSX.Element => {
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;

    const { depositsTurnOffForLeavingOperator, paymentProviders } = configState;
    const viewJourney = paymentProviders.isNewPaymentJourneyEnabled ? 'multi-deposit' : paymentProviders.debitCard.type;

    if (depositsTurnOffForLeavingOperator.enabled) {
        return (
            <OperatorTurnOffDeposits
                operatorName={depositsTurnOffForLeavingOperator.operatorName}
                isSignup={true}
                date={depositsTurnOffForLeavingOperator.date}
            />
        );
    }

    switch (viewJourney) {
        case 'realex':
            return (
                <DepositSignupLayout
                    skipAction={signUpState.toggleAside}
                    signUpState={signUpState}
                >
                    <RealexJourney isSignup={true} />
                </DepositSignupLayout>
            );
        case 'praxis':
            return (
                <DepositSignupLayout
                    skipAction={signUpState.toggleAside}
                    signUpState={signUpState}
                >
                    <PraxisJourney isSignup={true} />
                </DepositSignupLayout>
            );
        case 'paysafe':
            return (
                <DepositSignupLayout
                    skipAction={signUpState.toggleAside}
                    signUpState={signUpState}
                >
                    <PaysafeJourney isSignup={true} />
                </DepositSignupLayout>
            );
        case 'multi-deposit':
            return (
                <DepositSignupLayout
                    skipAction={signUpState.toggleAside}
                    signUpState={signUpState}
                >
                    <MultiDepositJourney isSignup={true} />
                </DepositSignupLayout>
            );
        case null:
            return <div />;
    }
});
